import { Badge, ChoiceCard, Tooltip } from "@coval/ui"
import { AudioLines } from "lucide-react"

import {
  clampVoiceSpeed,
  getVoiceNaturalness,
  VOICE_SPEED_DEFAULT,
  VOICE_VOLUME_DEFAULT,
  type CovalVoice,
} from "@/types/ModelConfigs/CovalVoiceConstants"

import { AudioPreviewButton } from "./AudioPreviewButton"
import {
  getVoiceAccentLabel,
  getVoiceLanguageSummary,
  isMultilingualVoice,
} from "./personaVoiceLibraryUtils"
import { VOICE_SAMPLE_TOOLTIP } from "./SelectedVoiceSummary"

const HIGH_NATURALNESS_TOOLTIP =
  "This voice runs Coval's higher-realism synthesis, which sounds closest to a human speaker. That stack supports approximately 12 simultaneous connections, so a high-volume simulation run may go faster on a standard voice."

/**
 * The voice a persona has already been given, shown on its Voice card. The
 * picker lists its catalog as a table, so this is the only place it renders.
 */
export function VoiceOption({
  voice,
  previewUrl,
  voiceVolume,
  voiceSpeed,
  isSelected,
  isDisabled,
  currentlyPlayingAudioId,
  onPlayingChange,
  onSelect,
}: {
  voice: CovalVoice
  previewUrl?: string
  voiceVolume?: number
  voiceSpeed?: number
  isSelected: boolean
  isDisabled: boolean
  currentlyPlayingAudioId?: string | null
  onPlayingChange?: (playingId: string | null) => void
  onSelect: () => void
}) {
  const accentLabel = getVoiceAccentLabel(voice)
  const isHighNaturalness = getVoiceNaturalness(voice.name) === "high"
  const effectiveVolume = voiceVolume ?? VOICE_VOLUME_DEFAULT
  const effectiveSpeed =
    clampVoiceSpeed(voiceSpeed, voice.name) ?? VOICE_SPEED_DEFAULT
  const presentation = `${voice.voicePresentation}${accentLabel ? `, ${accentLabel}` : ""}`
  const description = [
    presentation,
    isMultilingualVoice(voice) ? "Multilingual" : undefined,
    getVoiceLanguageSummary(voice),
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div role="group" aria-label={`${voice.displayName} voice option`}>
      <ChoiceCard
        title={voice.displayName}
        description={description}
        // Name the control by voice and presentation only, so the language
        // reach in the description does not bloat its accessible name.
        aria-label={`${voice.displayName} ${presentation}`}
        selected={isSelected}
        disabled={isDisabled}
        onSelect={onSelect}
        // The Voice card is already the container, so the chosen voice keeps
        // the outline without adopting a selected card's fill.
        variant="plain"
        className="gap-2 p-2"
        trailing={
          <AudioPreviewButton
            previewUrl={previewUrl}
            audioLabel={voice.displayName}
            volume={effectiveVolume}
            playbackRate={effectiveSpeed}
            currentlyPlayingId={currentlyPlayingAudioId}
            onPlayingChange={onPlayingChange}
            tooltipContent={VOICE_SAMPLE_TOOLTIP}
          />
        }
      >
        {isHighNaturalness && (
          <div className="flex flex-wrap gap-1">
            <VoiceNaturalnessBadge />
          </div>
        )}
      </ChoiceCard>
    </div>
  )
}

/**
 * Marks a voice that runs the higher-realism synthesis stack. Deliberately
 * neutral rather than a warning: it describes what the voice is good at, and
 * the concurrency ceiling that comes with it is a footnote in the tooltip.
 */
export function VoiceNaturalnessBadge() {
  return (
    <Tooltip
      side="top"
      content={
        <div className="max-w-xs leading-5">{HIGH_NATURALNESS_TOOLTIP}</div>
      }
    >
      {/* A span, not a button: this badge renders inside the voice card's
          select button, and buttons cannot nest. */}
      <span
        tabIndex={0}
        role="note"
        aria-label="What most natural means"
        className="inline-flex rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coval-contrast"
      >
        <Badge variant="default" className="h-5 gap-1 px-1.5 text-[11px]">
          <AudioLines className="h-3 w-3" />
          Most natural
        </Badge>
      </span>
    </Tooltip>
  )
}
